import React, { ReactNode, useEffect, useState } from 'react';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication, EventType, AuthenticationResult } from '@azure/msal-browser';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { msalConfig, msalConfigured } from './msalConfig';
import { DevAuthProvider } from './DevAuthProvider';
import { DEV_AUTH_BYPASS } from './devAuthFlag';
import { AuthSessionProvider, DevAuthSessionProvider } from './useAuth';
import { googleClientId, googleConfigured } from './googleConfig';

/**
 * Instantiates the MSAL PublicClientApplication once and wraps the app in
 * `MsalProvider`, then composes Entra with an optional direct Google session.
 * Google Identity Services is mounted only when its client id is configured.
 *
 * MSAL v3 requires `initialize()` to complete before any auth API is used, so we
 * gate rendering on that promise. When `REACT_APP_CLUBHOUSE_DEV_AUTH_BYPASS=true`, MSAL is
 * swapped for `DevAuthProvider`, a mock that reports a fixed dev test user as
 * signed in (see the API's dev auth handler). Intended for local development only.
 */
export const pca = new PublicClientApplication(msalConfig);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [ready, setReady] = useState(false);
  const [entraIdToken, setEntraIdToken] = useState<string>();

  useEffect(() => {
    if (DEV_AUTH_BYPASS) return;
    if (!msalConfigured) {
      // Fail loudly in development if the tenant isn't configured yet.
      // eslint-disable-next-line no-console
      console.warn(
        'Entra External ID is not configured. Set REACT_APP_CLUBHOUSE_MSAL_CLIENT_ID, ' +
          'REACT_APP_CLUBHOUSE_MSAL_AUTHORITY, and REACT_APP_CLUBHOUSE_MSAL_REDIRECT_URI in .env.local.',
      );
    }

    let mounted = true;
    const initializeAuth = async () => {
      try {
        await pca.initialize();
        pca.addEventCallback((event) => {
          if (
            event.eventType === EventType.LOGIN_SUCCESS &&
            event.payload &&
            (event.payload as AuthenticationResult).account
          ) {
            const result = event.payload as AuthenticationResult;
            pca.setActiveAccount(result.account);
            if (result.idToken) setEntraIdToken(result.idToken);
          }
        });

        const redirectResult = await pca.handleRedirectPromise();
        if (redirectResult?.account) {
          pca.setActiveAccount(redirectResult.account);
          if (redirectResult.idToken) setEntraIdToken(redirectResult.idToken);
        } else if (!pca.getActiveAccount()) {
          pca.setActiveAccount(pca.getAllAccounts()[0] ?? null);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Entra redirect initialization failed', error);
        // Even if initialization fails (e.g. unconfigured tenant), let the app
        // render so the login screen can show a setup message.
      } finally {
        if (mounted) setReady(true);
      }
    };

    void initializeAuth();
    return () => {
      mounted = false;
    };
  }, []);

  if (DEV_AUTH_BYPASS) {
    return (
      <DevAuthProvider>
        <DevAuthSessionProvider>{children}</DevAuthSessionProvider>
      </DevAuthProvider>
    );
  }

  if (!ready) {
    return (
      <div className="auth-loading" style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} aria-label="Signing in" />
      </div>
    );
  }

  const sessions = <AuthSessionProvider entraIdToken={entraIdToken}>{children}</AuthSessionProvider>;

  return (
    <MsalProvider instance={pca}>
      {googleConfigured ? (
        <GoogleOAuthProvider clientId={googleClientId}>{sessions}</GoogleOAuthProvider>
      ) : sessions}
    </MsalProvider>
  );
};
